import {
  ComponentProps,
  createContext,
  type ReactNode,
  use,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  Toast as ToastComponent,
  ToastIntentType,
} from '../../components/Toast/Toast';

type Toast = Pick<
  ComponentProps<typeof ToastComponent>,
  'content' | 'title' | 'label'
> & {
  id: string;
};

type AddToastArgs = Omit<Toast, 'id'>;

type ToastContextType = {
  positive: ({ ...args }: AddToastArgs) => void;
  negative: ({ ...args }: AddToastArgs) => void;
  info: ({ ...args }: AddToastArgs) => void;
  warning: ({ ...args }: AddToastArgs) => void;
};

type ToastContainerProps = {
  toasts: Toast[];
  onDismissToast: (id: string) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const ToastContainer = ({ toasts, onDismissToast }: ToastContainerProps) => {
  return createPortal(
    <div>
      {toasts.map(({ id, label, ...args }) => (
        <ToastComponent
          key={id}
          aria-label={label}
          label={label}
          onOpenChange={() => onDismissToast(id)}
          {...args}
        />
      ))}
    </div>,
    document.body,
  ) as ReactNode;
};

type ToasterProps = {
  children: ReactNode;
};

export const Toaster = ({ children }: ToasterProps) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    ({ ...args }: AddToastArgs & { intent: ToastIntentType }) => {
      const id = crypto.randomUUID();
      setToasts((prevToasts) => [...prevToasts, { ...args, id }]);
    },
    [],
  );

  const removeToast = useCallback(
    (id: string) =>
      setToasts((prevToasts) =>
        prevToasts.filter((toast: Toast) => toast.id !== id),
      ),
    [],
  );

  // Stable identities so consumers can safely put these in effect deps.
  const value = useMemo<ToastContextType>(
    () => ({
      positive: (args: AddToastArgs) =>
        addToast({ ...args, intent: 'positive' }),
      negative: (args: AddToastArgs) =>
        addToast({ ...args, intent: 'negative' }),
      info: (args: AddToastArgs) => addToast({ ...args, intent: 'info' }),
      warning: (args: AddToastArgs) => addToast({ ...args, intent: 'warning' }),
    }),
    [addToast],
  );

  return (
    <ToastContext value={value}>
      <ToastContainer toasts={toasts} onDismissToast={removeToast} />
      {children}
    </ToastContext>
  );
};

export const useToast = () => {
  const context = use(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  return context;
};
